import DSAuth from '../DSAuth';
import { AuthChoice } from '../data/AuthChoice';
import { GenericRequest, HTTPFetchResponse } from '../data/AuthTypes';
import { stringConstants, headerConstants } from '../../AuthConstants';
import { SentryLogger } from '../../../sentryLogging/SentryLogger';
import { Severity } from '@sentry/browser';
import {
	Authenticator,
	AuthenticateError,
	logEvent,
	analyticsConstants,
} from './Authenticator';
import { Logger } from '../../Logger';
import { AotCategory, logConstants } from '../../../utils/Constants';
import { PrimaryTokenFetchFailure } from '../../AuthErrors';
import { HttpStatusCodes } from '../../../utils/http/HttpConstants';
import { environment } from '../../environment';

export default class IntegratedWindowsAuthenticator implements Authenticator {
	async getToken(
		authProtocol: AuthChoice,
		authProtocolsRequest: GenericRequest,
		id: string,
		referenceUri: string,
		transactionId: string
	): Promise<HTTPFetchResponse | AuthenticateError> {
		if (!(await environment.isIWAEnabled(referenceUri))) {
			Logger.getInstance().writeAot(
				AotCategory.Info,
				`Integrated Windows Authentication (IWA) Protocol is not supported`
			);

			return AuthenticateError.UnsuitableProtocol;
		}

		SentryLogger.getInstance().addBreadCrumb({
			category: 'IntegratedWindowsAuthenticator',
			level: Severity.Info,
			message: 'getToken for IntegratedWindowsAuthenticator',
		});

		const iwaLocation = authProtocol.getLocation();
		return await this.fetchPrimaryToken(
			authProtocolsRequest.body,
			iwaLocation,
			transactionId,
			referenceUri,
			id
		);
	}

	private async fetchPrimaryToken(
		body: string,
		iwaLocation: string,
		transactionId: string,
		referenceUri: string,
		id: string
	): Promise<HTTPFetchResponse | AuthenticateError> {
		const headers = DSAuth.getInstance().addBasicHeaders(
			{
				Accept: headerConstants.TOKEN_RESPONSE_CONTENT_TYPE,
				[headerConstants.CONTENT_TYPE]: headerConstants.TOKEN_REQUEST_CONTENT_TYPE,
			},
			transactionId
		);

		let response;
		try {
			response = await DSAuth.getInstance().fetch(
				stringConstants.POST,
				iwaLocation,
				headers,
				body,
				referenceUri,
				true,
				''
			);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}: PRIMARY_TOKEN_REQUEST_FAILURE: ${e.message}`
			);
			throw new PrimaryTokenFetchFailure(e);
		}

		if (response.statusCode === HttpStatusCodes.HTTP_STATUS_OK) {
			logEvent(analyticsConstants.LABEL_INTEGRATEDWINDOWS_AUTHENTICATOR, 1);
			return response;
		}

		// Could be due to invalid creds, settings not configured for IWA, server in wrong zone, server misconfigured
		if (response.statusCode === HttpStatusCodes.HTTP_STATUS_DENIED) {
			Logger.getInstance().writeAot(
				AotCategory.Error,
				`Failed to fetch primary token - Unauthorized response received`
			);
			return AuthenticateError.UnsuitableProtocol;
		}

		const error = `Failed to fetch primary token - unexpected HTTP response status code: ${response.statusCode}`;
		Logger.getInstance().write(
			logConstants.ERROR,
			`${id}: ${error}`,
			AotCategory.Error,
			error
		);
		throw new PrimaryTokenFetchFailure(new Error(error));
	}
}
